/**
 * Catalogue des schémas de garde pratiqués en France.
 *
 * **Source unique.** L'enum PostgreSQL, le DTO de l'API, le moteur de calcul et le
 * formulaire de création décrivaient chacun leur propre liste ; quatre listes qui
 * divergent silencieusement, c'est un schéma proposé par le web que le serveur refuse,
 * ou un libellé faux dans un dossier remis à un avocat. Tout part d'ici.
 *
 * Le vocabulaire juridique parle de **résidence** de l'enfant — le mot « garde » a quitté
 * le code civil en 2002 — mais l'usage l'a conservé, et le produit s'adresse à des parents,
 * pas à des juristes. On garde donc « schéma de garde » dans l'interface, et les
 * dénominations exactes (droit de visite et d'hébergement, résidence alternée) dans les
 * détails.
 */
/**
 * Identifiant du moteur de calcul, qui parle en chiffres comme les conventions.
 * Distinct de l'identifiant stocké pour la seule raison rappelée ci-dessus.
 */
export const CUSTODY_SCHEDULE_TYPES = [
    'alternating_week',
    '2255',
    '223',
    '3443',
    'fortnight',
    '95',
    '104',
    'alternate_weekends',
    'extended_weekends',
    'visits_only',
    'holidays_only',
    'third_party',
    'custom',
];
export const SCHEMAS_DE_GARDE = [
    {
        kind: 'alternating_week',
        type: 'alternating_week',
        libelle: 'Semaine alternée (7/7)',
        detail: 'Une semaine chez l’un, une semaine chez l’autre. Le schéma le plus répandu.',
        famille: 'alternance',
        genere: true,
    },
    {
        kind: 'two_two_five_five',
        type: '2255',
        libelle: '2-2-5-5',
        detail: 'Deux jours fixes chez chacun chaque semaine, puis cinq jours incluant le week-end.',
        famille: 'alternance',
        genere: true,
    },
    {
        kind: 'two_two_three',
        type: '223',
        libelle: '2-2-3',
        detail: 'Deux nuits, deux nuits, trois nuits : pensé pour les enfants jeunes.',
        famille: 'alternance',
        genere: true,
    },
    {
        kind: 'three_four_four_three',
        type: '3443',
        libelle: '3-4-4-3',
        detail: 'Bascule en milieu de semaine, sur un cycle de deux semaines.',
        famille: 'alternance',
        genere: true,
    },
    {
        kind: 'fortnight',
        type: 'fortnight',
        libelle: 'Quinzaine alternée (14/14)',
        detail: 'Deux semaines chez chacun : adolescents, ou éloignement géographique.',
        famille: 'alternance',
        genere: true,
    },
    {
        kind: 'nine_five',
        type: '95',
        libelle: 'Alternance 9/5',
        detail: 'Neuf nuits chez l’un, cinq chez l’autre, sur un cycle de deux semaines.',
        famille: 'alternance_inegale',
        genere: true,
    },
    {
        kind: 'ten_four',
        type: '104',
        libelle: 'Alternance 10/4',
        detail: 'Dix nuits chez l’un, quatre chez l’autre, sur un cycle de deux semaines.',
        famille: 'alternance_inegale',
        genere: true,
    },
    {
        kind: 'alternate_weekends',
        type: 'alternate_weekends',
        libelle: 'Week-ends alternés (1er, 3e, 5e)',
        detail: 'Droit de visite et d’hébergement classique : un week-end sur deux, vendredi à dimanche.',
        famille: 'residence_principale',
        genere: true,
    },
    {
        kind: 'extended_weekends',
        type: 'extended_weekends',
        libelle: 'Week-ends alternés élargis',
        detail: 'Droit de visite élargi : un week-end sur deux, du vendredi soir au lundi matin.',
        famille: 'residence_principale',
        genere: true,
    },
    {
        kind: 'visits_only',
        type: 'visits_only',
        libelle: 'Visites sans hébergement',
        detail: 'Droit de visite réduit : des journées, sans nuitée. Chaque visite est posée au calendrier.',
        famille: 'residence_principale',
        genere: false,
    },
    {
        kind: 'holidays_only',
        type: 'holidays_only',
        libelle: 'Vacances scolaires uniquement',
        detail: 'Droit de visite longue distance : pas de week-ends réguliers, l’essentiel des vacances.',
        famille: 'residence_principale',
        genere: false,
    },
    {
        kind: 'third_party',
        type: 'third_party',
        libelle: 'Résidence chez un tiers',
        detail: 'L’enfant réside chez un tiers ; les temps de chaque parent sont posés un à un.',
        famille: 'autre',
        genere: false,
    },
    {
        kind: 'custom',
        type: 'custom',
        libelle: 'Sur mesure',
        detail: 'Aucune génération automatique : le calendrier se construit période par période.',
        famille: 'autre',
        genere: false,
    },
];
/** Identifiants stockés, dans l'ordre du catalogue. */
export const SCHEMAS_DE_GARDE_KINDS = SCHEMAS_DE_GARDE.map((s) => s.kind);
const PAR_KIND = new Map(SCHEMAS_DE_GARDE.map((s) => [s.kind, s]));
const PAR_TYPE = new Map(SCHEMAS_DE_GARDE.map((s) => [s.type, s]));
export const schemaDeGarde = (kind) => PAR_KIND.get(kind);
/** Identifiant du moteur correspondant à l'identifiant stocké, et réciproquement. */
export const typeDuSchema = (kind) => schemaDeGarde(kind).type;
export const kindDuType = (type) => PAR_TYPE.get(type).kind;
/** Vrai si la chaîne est un schéma connu. Sert de garde aux entrées non typées. */
export const estSchemaDeGarde = (valeur) => PAR_KIND.has(valeur);
/** Vrai si le moteur sait produire les périodes de ce schéma sans exception explicite. */
export const schemaGenerable = (kind) => schemaDeGarde(kind).genere;
/**
 * Les schémas où l'enfant réside alternativement chez l'un et chez l'autre — les seuls
 * qu'on puisse appeler « garde alternée » sans dire quelque chose de faux. L'alternance
 * inégalitaire en fait partie : un 9/5 reste juridiquement une résidence alternée.
 */
export const schemaAlterne = (kind) => schemaDeGarde(kind).famille === 'alternance' ||
    schemaDeGarde(kind).famille === 'alternance_inegale';
/**
 * Libellé affiché d'un schéma, nidation comprise.
 *
 * La **nidation** (l'enfant reste au domicile, les parents alternent) ne définit pas un
 * partage du temps : elle se superpose à n'importe quel schéma. En faire une valeur du
 * catalogue obligerait à dupliquer les treize autres. C'est donc un paramètre, et il
 * n'apparaît qu'ici, à l'affichage.
 */
export function libelleSchemaDeGarde(kind, options = {}) {
    const libelle = estSchemaDeGarde(kind) ? schemaDeGarde(kind).libelle : kind;
    return options.nidation ? `${libelle} · en nidation` : libelle;
}
//# sourceMappingURL=schemas.js.map